import {ArrowDirectionType, ArrowLineType} from '../../../../constants/dataEnum';
import {getCenterPosition, mapArrowDefinitions, zeroOffset} from '../../../../lib/arrowHelper';
import {resolveIntl} from '../../../../stores/business/commonBusiness';

const intl = resolveIntl();

const sampleSourceOffset = {x: 0, y: -20};
const aliquotOffset = {x: -10, y: -15};

const serialPlateOffsetTop = {x: 0, y: -30};
const serialPlateOffsetBottom = {x: 0, y: 20};

const mapArrowDefProps = (
  componentFrom,
  componentTo,
  offsetFrom = zeroOffset,
  offsetTo = zeroOffset,
  line = ArrowLineType.BOW,
  withGap = true,
  offsetBow = 80,
) => {
  const lineProps = line !== ArrowLineType.EDGED ? {offset: offsetBow} : {};

  const options = {
    sourceOffset: offsetFrom,
    destinationOffset: offsetTo,
    line,
    gap: withGap && line === ArrowLineType.BOW ? 20 : 0,
    otherProps: {...lineProps, direction: ArrowDirectionType.RIGHT},
  };

  return mapArrowDefinitions(componentFrom, componentTo, options);
};

const getArrowDefSerialPlate = (serialPlateRefs, serialPlateRatioRef) => {
  const processedSerialPlateArrowTop = [];
  const processedSerialPlateArrowBottom = [];

  let prevTubePositionTop;
  let prevTubePositionBottom;

  for (let i = 0; i < 4; i += 1) {
    const currentTubeRawPositionTop = serialPlateRefs?.current[i]?.getBoundingClientRect();
    const currentTubeRawPositionBottom = serialPlateRatioRef?.current[i]?.getBoundingClientRect();

    const currentTubePosition = getCenterPosition(currentTubeRawPositionTop, currentTubeRawPositionTop.y);
    const currentTubePositionBottom = getCenterPosition(
      currentTubeRawPositionBottom,
      currentTubeRawPositionBottom.bottom,
    );

    if (i !== 0) {
      const serialPlateArrowTop = mapArrowDefProps(
        prevTubePositionTop,
        currentTubePosition,
        serialPlateOffsetTop,
        serialPlateOffsetTop,
      );

      const serialPlateArrowBottom = mapArrowDefProps(
        prevTubePositionBottom,
        currentTubePositionBottom,
        serialPlateOffsetBottom,
        serialPlateOffsetBottom,
        ArrowLineType.EDGED,
      );

      processedSerialPlateArrowTop.push(serialPlateArrowTop);
      processedSerialPlateArrowBottom.push(serialPlateArrowBottom);
    }

    prevTubePositionTop = currentTubePosition;
    prevTubePositionBottom = currentTubePositionBottom;
  }

  return [processedSerialPlateArrowTop, processedSerialPlateArrowBottom];
};

export const generateArrowDefinition = (
  serialPlateRatioRef,
  serialPlateRefs,
  diluentSourceRef,
  sampleSourceRef,
  aliquotRef,
) => {
  const diluentSourcePos = diluentSourceRef.current.getBoundingClientRect();
  const sampleSourcePos = sampleSourceRef.current.getBoundingClientRect();
  const aliquotPos = aliquotRef.current.getBoundingClientRect();

  const [serialPlateArrowTop, serialPlateArrowBottom] = getArrowDefSerialPlate(serialPlateRefs, serialPlateRatioRef);

  // reduced the x value by a few points so that the arrow have gaps between them
  const firstTopSerialPlateTubePosition = {x: serialPlateArrowTop[0].fromX - 20, y: serialPlateArrowTop[0].fromY};
  const firstBottomSerialPlateTubePosition = {x: serialPlateArrowBottom[0].fromX, y: serialPlateArrowBottom[0].fromY};

  // get center position of each elements to position the arrow
  const aliquotPosition = getCenterPosition(aliquotPos, aliquotPos.y);
  const sampleSourcePosition = getCenterPosition(sampleSourcePos, sampleSourcePos.y);
  const diluentSourcePosition = getCenterPosition(diluentSourcePos, firstBottomSerialPlateTubePosition.y);
  // diluent source uses y value from serial plate to even out the height

  // mapped arrow def props by their position
  const dilluentSourceArrowProps = mapArrowDefProps(
    diluentSourcePosition,
    firstBottomSerialPlateTubePosition,
    zeroOffset,
    zeroOffset,
    ArrowLineType.EDGED,
  );

  const sampleSourceToAliquotProps = mapArrowDefProps(
    sampleSourcePosition,
    aliquotPosition,
    sampleSourceOffset,
    aliquotOffset,
  );

  const aliquotToSerialPlateArrowProps = mapArrowDefProps(
    aliquotPosition,
    {x: firstTopSerialPlateTubePosition.x - 20, y: firstTopSerialPlateTubePosition.y},
    aliquotOffset,
    zeroOffset,
  );

  dilluentSourceArrowProps.textBoxLabel = intl.formatMessage({
    id: 'protocolEditor.serialDilution.volumeTextBox.diluentSource.label',
  });

  const dilluentSourceArrow = {
    key: 'diluentVolume',
    field: 'diluentVolume',
    props: [dilluentSourceArrowProps],
  };

  const sampleSourceToAliquotArrow = {
    key: 'sampleVolumeAliquot',
    field: 'sampleVolume',
    props: [sampleSourceToAliquotProps],
  };

  const aliquotToSerialPlateArrow = {
    key: 'aliquotVolume',
    field: 'aliquotVolume',
    props: [aliquotToSerialPlateArrowProps],
  };

  const serialPlateTopArrow = {
    key: 'stepSampleVolume',
    field: 'stepSampleVolume',
    props: serialPlateArrowTop,
  };

  const serialPlatBottomArrow = {
    key: 'stepDiluentVolume',
    field: 'stepDiluentVolume',
    props: serialPlateArrowBottom,
  };

  return [
    dilluentSourceArrow,
    serialPlatBottomArrow,
    serialPlateTopArrow,
    sampleSourceToAliquotArrow,
    aliquotToSerialPlateArrow,
  ];
};

export default generateArrowDefinition;
